/**
 * Pure decision function for {@link AuthController}.
 *
 * Extracted so the redirect-guard contract is unit-testable without rendering
 * React / oidc-client-ts. It encodes the same guards that `react-oidc-context`'s
 * own `useAutoSignin` / `withAuthenticationRequired` apply — `hasAuthParams()`,
 * `activeNavigator`, `isLoading`, and a once-only `hasTriedSignin` latch — which
 * the hand-rolled AuthController previously omitted.
 *
 * Why the guards matter (Sentry CUSTOMER-UI-3T): an expired-token user on a
 * protected route triggered `signinRedirect()`. When the OIDC navigator closed
 * while the user was still unauthenticated (e.g. iOS WebKit lost the OIDC state
 * to storage partitioning on the callback), the effect re-fired the redirect on
 * every render — a loop that overflowed iOS WebKit's small call stack
 * (`RangeError: Maximum call stack size exceeded`). The `hasTriedSignin` latch
 * and the `hasAuthParams` / `activeNavigator` guards make the redirect fire at
 * most once per auth-resolution cycle.
 */

import { isExpectedSessionEndError } from "./resolveAuthFailureReport";

export interface AuthActionInput {
  /** OIDC user load / navigator in flight. */
  isLoading: boolean;
  /** Non-undefined while a signin/signout navigator (redirect/popup/silent) runs. */
  activeNavigator: string | undefined;
  isAuthenticated: boolean;
  isPublicRoute: boolean;
  isAutoLogin: boolean;
  /** OIDC error from react-oidc-context, if any. */
  error: unknown;
  /** True when the URL carries `?code|error` + `state` (a Keycloak callback). */
  hasAuthParams: boolean;
  /** True once this mount has already initiated a signin redirect. */
  hasTriedSignin: boolean;
  /**
   * True once the cross-page-load automatic-redirect budget is spent (see
   * signinRedirectAttempts.ts). `hasTriedSignin` resets on every page load,
   * so a failure that recurs on every Keycloak callback (persistent realm
   * error, iOS storage-partitioning state loss) would otherwise loop
   * app↔Keycloak at machine speed across page loads. When exhausted,
   * AuthController renders ParkedSignIn — a "Sign in" CTA that resets the
   * budget and calls signinRedirect directly, because a deliberate human
   * click is not a loop. The splash is NOT the recovery: after a soft
   * navigation from a public page its veil is already gone.
   */
  signinRedirectExhausted: boolean;
  /**
   * Whether the stored access token is expired (or absent). `user?.expired`
   * can be `undefined` when `expires_at` is missing — treat that as expired
   * so we never "wait" indefinitely on an unknowable token.
   */
  tokenExpired: boolean;
  /**
   * True when a stored user with a refresh token exists — i.e. a silent
   * renewal has a realistic chance of succeeding without a full-page
   * Keycloak round-trip. (The iframe fallback without a refresh token is
   * unreliable under third-party-cookie blocking, so it doesn't qualify.)
   */
  hasRenewableUser: boolean;
  /** True once this mount has already attempted the recovery silent renewal. */
  hasTriedSilentRenew: boolean;
}

export type AuthAction =
  /**
   * `redirect-budget-exhausted` marks the deliberate parked state, which
   * AuthController both renders as ParkedSignIn (a "Sign in" CTA that resets
   * the budget and calls signinRedirect directly — the recovery, in place of
   * the splash's "Reload app") and reports to Sentry, since nothing organic
   * throws there.
   * `renewal-error-token-still-valid` keeps the app rendered through a
   * transient renewal failure while the access token still works; renewal is
   * re-attempted (mid-window re-try, refocus/online listeners, the
   * last-chance attempt at expiry) and the ERROR state is cleared by the
   * next successful USER_LOADED.
   */
  | {
      type: "wait";
      reason?: "redirect-budget-exhausted" | "renewal-error-token-still-valid";
    }
  | { type: "ready" }
  | { type: "authenticated-public" }
  /**
   * Attempt ONE silent renewal (refresh-token grant) before falling back to
   * the full-page redirect. Success re-authenticates in place — no page
   * reload, no lost in-progress work; failure dispatches an ERROR that
   * re-enters the error branch (now with `tokenExpired: true`) and redirects.
   */
  | { type: "silent-renew" }
  | { type: "signin"; reason: "oidc-error" | "token-expired" };

/**
 * react-oidc-context tags every error it dispatches with a `source`:
 * "renewSilent" for the automatic SilentRenewService failure listener,
 * the method name (e.g. "signinSilent") for failed context-method calls,
 * "signinCallback"/"signoutCallback" for callback processing. Renewal
 * sources are recoverable states, not sign-in failures.
 */
function isRenewalSourcedError(error: unknown): boolean {
  const source =
    error && typeof error === "object"
      ? (error as { source?: unknown }).source
      : undefined;
  return source === "renewSilent" || source === "signinSilent";
}

function errorMessageOf(error: unknown): string {
  return error && typeof error === "object"
    ? String((error as { message?: unknown }).message ?? "")
    : "";
}

export function resolveAuthAction({
  isLoading,
  activeNavigator,
  isAuthenticated,
  isPublicRoute,
  isAutoLogin,
  error,
  hasAuthParams,
  hasTriedSignin,
  signinRedirectExhausted,
  tokenExpired,
  hasRenewableUser,
  hasTriedSilentRenew,
}: AuthActionInput): AuthAction {
  // Never act while auth state is settling or while a navigator is already
  // running — let AuthProvider finish first. (Mirrors react-oidc-context's
  // own guards.)
  if (isLoading || activeNavigator) {
    return { type: "wait" };
  }

  // The error check must come BEFORE the hasAuthParams guard. When Keycloak
  // redirects back with an error callback (?error=temporarily_unavailable&
  // error_description=authentication_expired&state=...), react-oidc-context's
  // signinCallback() throws, so onSigninCallback — the only place the auth
  // params are cleaned from the URL — never runs. hasAuthParams therefore
  // stays true forever; waiting on it here froze the app on the splash
  // screen. An error means callback processing is already over (it failed),
  // so act on it.
  if (error) {
    // Public routes (register / logged-out) must render even when OIDC errors,
    // e.g. Keycloak unreachable on localhost during development.
    if (isPublicRoute) return { type: "ready" };
    // A TRANSIENT renewal failure with validity left on the access token is
    // NOT a reason to interrupt the user: the app still works, renewal gets
    // re-attempted (mid-window re-try, refocus/online listeners, and the
    // last-chance attempt when the token actually expires), and a successful
    // renewal clears the error via USER_LOADED. Bouncing here is what threw
    // actively-editing users out to Keycloak (CUSTOMER-UI-56/57). A
    // DEFINITIVE session-end (invalid_grant / "Token is not active") is
    // excluded: the refresh token is dead, and for a revoked session,
    // lingering access would be a security hole — redirect immediately.
    if (
      isRenewalSourcedError(error) &&
      !tokenExpired &&
      !isExpectedSessionEndError(errorMessageOf(error))
    ) {
      return { type: "wait", reason: "renewal-error-token-still-valid" };
    }
    // Protected route + OIDC error → sign in, but only once per page load
    // (hasTriedSignin) and within the cross-page-load redirect budget
    // (signinRedirectExhausted) so a persistent error can't loop.
    if (hasTriedSignin) return { type: "wait" };
    if (signinRedirectExhausted) {
      return { type: "wait", reason: "redirect-budget-exhausted" };
    }
    return { type: "signin", reason: "oidc-error" };
  }

  // While returning FROM Keycloak (?code|error + state in the URL) with no
  // error reported yet, AuthProvider is still processing the callback.
  if (hasAuthParams) {
    return { type: "wait" };
  }

  if (isAuthenticated && isPublicRoute && !isAutoLogin) {
    return { type: "authenticated-public" };
  }

  if (!isAuthenticated && !isPublicRoute) {
    // A stored-but-expired user with a refresh token (e.g. the app booted
    // after the access token lapsed, or expiry hit before any recovery ran)
    // can usually be re-authenticated in place via one silent renewal —
    // no full-page Keycloak round-trip, no lost in-progress work. Allowed
    // even when the redirect budget is exhausted: it navigates nowhere, is
    // latched to once per mount, and may unpark the app. Failure lands in
    // the error branch above (tokenExpired true) and redirects as before.
    if (hasRenewableUser && !hasTriedSilentRenew) {
      return { type: "silent-renew" };
    }
    if (hasTriedSignin) return { type: "wait" };
    if (signinRedirectExhausted) {
      return { type: "wait", reason: "redirect-budget-exhausted" };
    }
    return { type: "signin", reason: "token-expired" };
  }

  return { type: "ready" };
}
